import {
  NoPermissionOrNotFound,
  NotFoundPage,
} from '@pique/component/not-found-page';
import { useAsyncCallback } from '@pique/core/hooks/pique-async-hooks';
import { useLiveData, useService, WorkspacesService } from '@toeverything/infra';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { SignOutModal } from '../components/pique/sign-out-modal';
import { RouteLogic, useNavigateHelper } from '../hooks/use-navigate-helper';
import { AuthService } from '../modules/cloud';
import { SignIn } from './sign-in';

export const PageNotFound = ({
  noPermission,
}: {
  noPermission?: boolean;
}): ReactElement => {
  const authService = useService(AuthService);
  const workspacesService = useService(WorkspacesService);
  const account = useLiveData(authService.session.account$);
  const workspaces = useLiveData(workspacesService.list.workspaces$);
  const { jumpToIndex } = useNavigateHelper();
  const [open, setOpen] = useState(false);

  // Simple loop detection - track navigation attempts
  const [navigationAttempts, setNavigationAttempts] = useState(0);
  const MAX_NAVIGATION_ATTEMPTS = 2;

  const handleBackButtonClick = useCallback(() => {
    // If we've tried multiple times and there are no workspaces,
    // we're likely in a loop - don't attempt navigation
    if (navigationAttempts >= MAX_NAVIGATION_ATTEMPTS && workspaces.length === 0) {
      console.warn('Preventing potential infinite loop between 404 and index - staying on 404 page');
      return;
    }

    setNavigationAttempts(prev => prev + 1);
    jumpToIndex(RouteLogic.REPLACE);
  }, [jumpToIndex, navigationAttempts, workspaces.length]);

  const handleOpenSignOutModal = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const onConfirmSignOut = useAsyncCallback(async () => {
    setOpen(false);
    await authService.signOut();
  }, [authService]);

  useEffect(() => {
    // Reset navigation attempts after a delay to allow recovery
    const timer = setTimeout(() => {
      setNavigationAttempts(0);
    }, 10000); // Reset after 10 seconds

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {noPermission ? (
        <NoPermissionOrNotFound
          user={account}
          onBack={handleBackButtonClick}
          onSignOut={handleOpenSignOutModal}
          signInComponent={<SignIn />}
        />
      ) : (
        <NotFoundPage
          user={account}
          onBack={handleBackButtonClick}
          onSignOut={handleOpenSignOutModal}
        />
      )}

      <SignOutModal
        open={open}
        onOpenChange={setOpen}
        onConfirm={onConfirmSignOut}
      />
    </>
  );
};

export const Component = PageNotFound;
